import React, { useEffect, useState } from "react";
// import OfferCard from "./offercard";
import { Inbox } from "lucide-react";

import { useSearchParams, useNavigate } from "react-router-dom";
import callApi from "../../../../utility/apiCaller";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedOffer as setOfferToStore } from "../../../../store/app/aaSlice";
import OfferCard from "../offerPage/offerCard2";
import useAddLead from "../../hooks/addLeadtoCRM";
import { setUserClickData } from "../../../../utility/setUserClickData";

// Respect the OS "reduce motion" setting for the load animation.
function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, []);
  return reduce;
}

// Each line/card rises + fades in, staggered by `delay` (ms).
function Reveal({ shown, delay = 0, reduce, className = "", children }) {
  const style = reduce
    ? undefined
    : {
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(20px)",
        transition:
          "opacity .6s cubic-bezier(.22,.7,.25,1), transform .6s cubic-bezier(.22,.7,.25,1)",
        transitionDelay: `${delay}ms`,
      };
  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
}

// utils/emiCalculator.js
export function calculateEMI(principal, annualRate, months) {
  const r = annualRate / 12 / 100; // monthly interest rate
  if (r === 0) return Math.round(principal / months); // no interest case

  return Math.round(
    (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1)
  );
}

async function withRetry(fn, retries = 2, delay = 2000) {
  let attempt = 0;
  while (attempt <= retries) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === retries) throw err;
      console.warn(`Retrying... attempt ${attempt + 1} failed`);
      await new Promise((r) => setTimeout(r, delay * (attempt + 1)));
      attempt++;
    }
  }
}

const DMI_ID = "68ad8ed5078abf6c677e4303";
const DMI = "DMI";

const HIDDEN_LENDER_IDS = [];
const HIDDEN_LENDER_NAME_REGEX = /^$/; 

const toFailedDisplayName = (name) =>
  /prefr/i.test(String(name ?? "")) ? "Prefr" : name;

export function getOfferEMI(offer, amount = null, tenor = null) {
  const principal = amount || offer.max_amount; 
  const months = tenor || offer.max_tenor; 
  const roi = offer.roi;

  return calculateEMI(principal, roi, months);
}

export default function LoanOfferPage({
  setCurrentStep,
  formdata,
  applicationId,
  responseId,
  MappedData,
  aaUrl,
  internalLeadId,
  setSelectedOffer,
}) {
  const offersData = useSelector((state) => state.app.allOffers);
  const [params] = useSearchParams();
  const dmioffersData = useSelector((state) => state.app.offers);
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user);
  
  const aaSlice = useSelector((state) => state?.aa);
  let leadId = aaSlice?.internalLeadId;
  const dispatch = useDispatch();
  const [expandedId, setExpandedId] = useState(null);
  const [failedList, setFailedList] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const addLead = useAddLead();
  
  // Flatten all offers
  let allOffers = Array.isArray(offersData)
    ? offersData.flatMap((item) => item.offers || [])
    : offersData?.offers
    ? offersData.offers
    : [];

  // ==========================================
  // 🚀 MOCK DATA INJECTION (Ensures UI works for Boss's Review)
  // If Redux is empty because we skipped APIs, force some dummy offers
  // ==========================================
  if (allOffers.length === 0) {
    allOffers = [
      { _id: "mock1", lender_name: "L&T Finance", max_amount: 800000, max_tenor: 60, roi: 10.75, priority: 1 },
      { _id: "mock2", lender_name: "IDFC FIRST Bank", max_amount: 750000, max_tenor: 60, roi: 11.00, priority: 2 }
    ];
  }

  const visibleOffers = allOffers
    .filter((offer) => !HIDDEN_LENDER_IDS.includes(offer?.lender_id))
    .sort((a, b) => (a?.priority ?? 999) - (b?.priority ?? 999));
    
  const visibleFailedList = (offersData?.failedList || [])
    .filter((name) => !HIDDEN_LENDER_NAME_REGEX.test(String(name ?? "")))
    .map(toFailedDisplayName);

  useEffect(() => {
    setExpandedId(visibleOffers?.[0]?._id);
  }, []);

  const highestAmountOffer =
    allOffers.length > 0
      ? allOffers.reduce((prev, current) =>
          current.max_amount > prev.max_amount ? current : prev
        )
      : null;

  const handleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleShowMoreOffers = () => {
    setUserClickData({
      event_name: `show-more-offers-multistep-pl-v1`,
      user_id: formdata?.mobile || visibleOffers?.[0]?.contact_phone || leadId || "unknown",
      affiliate_id: params.get("aff_id") || "No Aff_id found",
      utm_source: params.get("utm_source") || params.get("utm_medium"),
      utm_term: params.get("utm_term"),
    });
    setShowAll(true);
  };

  const [showIframe, setShowIframe] = useState(false);

  const reduceMotion = usePrefersReducedMotion();
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleClickNext = async () => {
    if (!highestAmountOffer) return;

    try {
      const payload = {
        lead: {
          dmi_offer: {
            tenure: highestAmountOffer.max_tenor,
            interest: highestAmountOffer.roi,
            amount: highestAmountOffer.max_amount,
          },
        },
      };

      // ==========================================
      // 🚀 MOCK UI FLOW (Immediately moves to Step 7)
      // ==========================================
      setSelectedOffer(payload.lead.dmi_offer);
      dispatch(setOfferToStore(payload.lead.dmi_offer));
      setCurrentStep(7);

      // ==========================================
      // 🛑 REAL API CALLS (COMMENTED OUT FOR NOW)
      // ==========================================
      /*
      await callApi(
        `v1/lead/${leadId}/update-lead-new`,
        "post",
        payload,
        "core", 
        user?.token
      );

      const updateResponse = await withRetry(() =>
        callApi(
          `v1/lead/${leadId}/update-lead-new`,
          "post",
          {
            lead: {
              is_claim_offer_click_completed: true,
              steps_completed: 6,
            },
          },
          "core",
          user?.token
        )
      );

      if (leadId) {
        try {
          await addLead({
            leadId: leadId,
            lead_type: "live",
            priority: 2,
            stage: "Offer",
          });
        } catch (error) {
          console.error("Error adding lead to CRM:", error);
        }
      }

      setCurrentStep((prev) => prev + 1);
      */
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  return visibleOffers.length === 0 ? (
    <div className="dm-offers-v9 max-w-[400px] mx-auto bg-white min-h-screen flex flex-col relative overflow-hidden px-4 pb-7">
      <div className="flex justify-center w-full items-center pt-6 pb-2">
        <img src="/assets/img/newLogo.png" alt="digitmoney logo" className="h-auto w-[140px]" />
      </div>

      <div className="bg-white rounded-[24px] border border-[#ededed] mt-[10dvh] px-6 py-8 text-center w-full">
        <Inbox className="mx-auto mb-4 h-[80px] w-[80px] text-[#12f280]" />
        <h2 className="text-[28px] text-[#1b1b1b] font-atkinson font-bold mb-2">Oops!</h2>
        <p className="text-[16px] text-[#8b8b8b] leading-[22px] font-atkinson mb-6">
          We currently have no pre-approved offers available for you. <br />
          Please check back later.
        </p>
        <p className="text-[#006f59] text-[16px] font-atkinson font-bold">
          Stay connected for exciting offers!
        </p>
      </div>

      <p className="text-center text-[#b3b3b3] text-[12px] leading-[14px] mt-6 px-4 font-atkinson">
        *These offers are subject to change at the discretion of the Bank / NBFC. Please ensure your
        details are updated to receive the latest offers.
      </p>
    </div>
  ) : (
    <div className="dm-offers-v9 max-w-[400px] mx-auto bg-white min-h-screen px-4 pb-[160px]">
      <div className="flex items-center pt-6 pb-1">
        <button
          type="button"
          onClick={() => setCurrentStep(3)} // Or "mock_routing" if you want them to go back to the switch
          aria-label="Go back"
          className="w-6 h-6 flex-none flex items-center justify-center"
        >
          <img src={process.env.PUBLIC_URL + "/assets/icons/left_arrow.png"} alt="" className="w-5 h-5 object-contain" />
        </button>
        <div className="flex-1">
          <Reveal shown={shown} reduce={reduceMotion} delay={0}>
            <h2 className="text-center font-atkinson font-bold text-[22px] tracking-tight text-[#1b1b1b]">
              Congratulations 🎉
            </h2>
          </Reveal>
        </div>
        <span className="w-6 flex-none" aria-hidden="true" />
      </div>

      <Reveal shown={shown} reduce={reduceMotion} delay={110}>
        <p className="text-center font-atkinson text-[13px] leading-[1.35] mt-[10px] text-[#1b1b1b]">
          <span className="text-[#ff7a1a] font-bold">{visibleOffers.length} lenders</span> matched your profile.
        </p>
      </Reveal>

      <Reveal shown={shown} reduce={reduceMotion} delay={160}>
        <p className="text-center font-atkinson text-[11px] leading-[1.4] mt-[6px] text-[#8b8b8b]">
          You're one step away from funding your{" "}
          <b className="text-[#1b1b1b]">“Purpose of Loan”</b>.
        </p>
      </Reveal>

      <Reveal shown={shown} reduce={reduceMotion} delay={220}>
        <div className="flex items-center justify-center gap-2 bg-[#d7f7e6] text-[#0b7a55] font-atkinson text-[11px] leading-[1.25] rounded-full px-4 py-3 mt-[18px]">
          ⏱ Most customers complete the next step in under 3 minutes.
        </div>
      </Reveal>

      {/* Offer cards */}
      <div>
        {(showAll ? visibleOffers : visibleOffers.slice(0, 3)).map((offer, i) => (
          <Reveal key={offer._id} shown={shown} reduce={reduceMotion} delay={340 + i * 130}>
            <OfferCard
              setCurrentStep={setCurrentStep}
              setSelectedOffer={setSelectedOffer}
              leadId={leadId}
              offer={offer}
              isExpanded={expandedId === offer._id}
              onExpand={() => handleExpand(offer._id)}
            />
          </Reveal>
        ))}
      </div>

      {!showAll && visibleOffers.length > 3 && (
        <button
          type="button"
          onClick={handleShowMoreOffers}
          className="w-full mt-4 border-[1.5px] border-[#1b1b1b] text-[#1b1b1b] font-atkinson font-bold text-[16px] rounded-[16px] py-[15px] active:scale-[0.99] transition-transform"
        >
          Show More Offers
        </button>
      )}

      {(offersData?.failedList || []).length > 0 && (
        <p className="font-atkinson text-[12px] text-[#8b8b8b] mt-6 mt-3 text-center">
          Note: There are no matching offers for your request from
        </p>
      )}
      {(offersData?.failedList || []).map((f, i) => (
        <OfferCard
          key={"failed-" + i}
          offer={f}
          failed={true}
          setCurrentStep={setCurrentStep}
          setSelectedOffer={setSelectedOffer}
        />
      ))}

      {/* Disclaimer — sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40">
        <div className="max-w-[400px] mx-auto bg-white px-4 pt-3 pb-4 border-t border-[#f0f0f0]">
          <p className="text-left text-[#b3b3b3] text-[12px] leading-[14px] font-atkinson">
            <b>Disclaimer:</b> These pre-approved offers are subject to change at discretion of Bank / NBFC after receiving
            all your documents and details. Final offer will be based on risk policy of Bank / NBFC. We do
            not guarantee that final offer will be same as Pre-approved offer.
            <br/><br />
            Note: You may receive calls from multiple lenders.
          </p>
        </div>
      </div>
    </div>
  );
}